import { FC, useEffect, useState } from 'react';
import { Alert } from 'react-daisyui';
import { Link, useNavigate } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import Stepper from '@/components/shared/Stepper/Stepper.tsx';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import * as z from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, useTranslation } from 'react-i18next';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';
import useRegisterUser from '@/api/useRegisterUser.ts';
import CheckboxInput from '@/components/functional/CheckboxInput/CheckboxInput.tsx';
import { passwordSchema } from '$/utils/validators/passwordSchema.ts';
import ContinueWithGoogleButton from '@/components/functional/Oauth/ContinueWithGoogleButton/ContinueWithGoogleButton.tsx';
import ContinueWithFacebookButton from '@/components/functional/Oauth/ContinueWithFacebookButton/ContinueWithFacebookButton.tsx';
import ContinueWithAppleButton from '@/components/functional/Oauth/ContinueWithAppleButton/ContinueWithAppleButton.tsx';
import { useAuthContext } from '@/context/AuthContext.tsx';
import OtpInput from '@/components/functional/OtpInput/OtpInput.tsx';

type Schema = SignUpStepSchema;

const steps = ['sign-up', 'verify-email'] as const;
const RegisterPage: FC = () => {
  return (
    <Stepper.Wrapper steps={steps} initialStep="sign-up">
      <Stepper.Step<typeof steps> step="sign-up">
        <StepSignUp />
      </Stepper.Step>
      <Stepper.Step<typeof steps> step="verify-email">
        <StepVerifyEmail />
      </Stepper.Step>
    </Stepper.Wrapper>
  );
};

export default RegisterPage;

const signUpStepSchema = z
  .object({
    email: z.string().min(1, { message: 'This field is required.' }).email('Invalid email'),
    password: passwordSchema,
    repeat_password: z.string().min(1, { message: 'This field is required.' }),
    terms: z.boolean().refine((value) => value, { message: 'You must agree to the terms and conditions' }),
  })
  .superRefine(({ repeat_password, password }, ctx) => {
    if (repeat_password !== password) {
      ctx.addIssue({
        code: 'custom',
        message: 'The passwords did not match',
        path: ['repeat_password'],
      });
    }
  });

type SignUpStepSchema = z.infer<typeof signUpStepSchema>;
function StepSignUp() {
  const { t } = useTranslation();
  const {
    mutateAsync: registerUser,
    isError: isErrorRegisterUser,
    isPending: isLoadingRegisterUser,
    error: errorRegisterUser,
  } = useRegisterUser();
  const { goToStep, setContextData } = useStepperContext<typeof steps, Schema>();
  const { control, handleSubmit } = useForm<SignUpStepSchema>({
    resolver: zodResolver(signUpStepSchema),
    defaultValues: {
      email: '',
      password: '',
      repeat_password: '',
      terms: false,
    },
  });

  const handleNextStep = (data: SignUpStepSchema) => {
    void (async () => {
      try {
        if (isLoadingRegisterUser) return;
        await registerUser(data);
        setContextData(data);
        goToStep('verify-email');
      } catch (error) {
        console.error(error);
      }
    })();
  };

  const INPUT_LABEL_CLASS =
    '!bg-[#FAFAFA] !border !border-[rgba(204,204,204,0.5)] !rounded-xl !min-h-[48px] font-dm text-sm';

  return (
    <BrandCardSideContent centerContent>
      <form className="flex flex-col gap-4 w-full" onSubmit={handleSubmit(handleNextStep)}>
        <div className="flex flex-col gap-2 py-6">
          <h1 className="font-newsreader font-bold text-2xl leading-[117%] text-[rgba(0,0,0,0.87)]">
            {t('registrationPage.title')}
          </h1>
          <p className="font-dm text-sm font-normal leading-[150%] tracking-[0.15px] text-[rgba(0,0,0,0.6)]">
            {t('registrationPage.description')}
          </p>
        </div>
        <div className="flex flex-col gap-4">
          {isErrorRegisterUser && <Alert status="error">{errorRegisterUser.message}</Alert>}
          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.email?.message}
                value={value}
                onChange={onChange}
                placeholder={t('label.email')}
                labelClassName={INPUT_LABEL_CLASS}
              />
            )}
          />
          <Controller
            control={control}
            name="password"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.password?.message}
                value={value}
                onChange={onChange}
                type="password"
                autoComplete="password"
                placeholder={t('label.password')}
                labelClassName={INPUT_LABEL_CLASS}
              />
            )}
          />
          <Controller
            control={control}
            name="repeat_password"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.repeat_password?.message}
                value={value}
                onChange={onChange}
                type="password"
                autoComplete="repeat_password"
                placeholder={t('label.repeat_password')}
                labelClassName={INPUT_LABEL_CLASS}
              />
            )}
          />
          <Controller
            control={control}
            name="terms"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <CheckboxInput
                onChange={onChange}
                checked={value}
                error={errors.terms?.message}
                label={
                  <Trans i18nKey="registrationPage.agreeToTerms">
                    I accept the
                    <Link target="_blank" className="text-link underline" to={Routes.PRIVACY_POLICY}>
                      Terms and Conditions
                    </Link>
                  </Trans>
                }
              />
            )}
          />
          <LoadingButton loading={isLoadingRegisterUser} type="submit" color="primary">
            {t('button.createAccount')}
          </LoadingButton>
          {import.meta.env.VITE_GOOGLE_LOGIN_ENABLED === 'true' && (
            <ContinueWithGoogleButton copy={t('button.registerWithGoogle')} />
          )}
          {import.meta.env.VITE_FACEBOOK_LOGIN_ENABLED === 'true' && (
            <ContinueWithFacebookButton copy={t('button.registerWithFacebook')} />
          )}
          {import.meta.env.VITE_APPLE_LOGIN_ENABLED === 'true' && (
            <ContinueWithAppleButton copy={t('button.registerWithApple')} />
          )}
        </div>
        <div className="flex justify-end">
          <Link className="font-dm font-semibold text-sm text-[#122346]" to={Routes.LOGIN}>
            {t('button.loginToExistingAccount')}
          </Link>
        </div>
      </form>
    </BrandCardSideContent>
  );
}

const verifyEmailStepSchema = z.object({
  code: z.string().min(1, { message: 'This field is required.' }),
});
type VerifyEmailStepSchema = z.infer<typeof verifyEmailStepSchema>;

const OTP_INPUT_CLASS = '!bg-[#FAFAFA] !border !border-[rgba(0,0,0,0.23)] !rounded text-[#1F2937] text-center font-dm';

function StepVerifyEmail() {
  const { t } = useTranslation();
  const { contextData } = useStepperContext<typeof steps, Schema>();
  const email = contextData?.email ?? '';
  const navigate = useNavigate();
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    if (!shouldRedirect) return;
    const timer = setTimeout(() => navigate(Routes.LOGIN), 3000);
    return () => clearTimeout(timer);
  }, [shouldRedirect, navigate]);

  const { control, handleSubmit } = useForm<VerifyEmailStepSchema>({
    resolver: zodResolver(verifyEmailStepSchema),
    defaultValues: { code: '' },
  });

  const {
    verifyEmailQuery: {
      mutateAsync: verifyEmail,
      isPending: isLoadingVerifyEmail,
      error: errorVerifyEmail,
      isError: isErrorVerifyEmail,
      isSuccess: isSuccessVerifyEmail,
    },
    resendVerificationCodeQuery: {
      mutateAsync: resendVerificationCode,
      isPending: isLoadingResend,
      error: errorResend,
      isError: isErrorResend,
      isSuccess: isSuccessResend,
    },
  } = useAuthContext();

  const handleVerify = (data: VerifyEmailStepSchema) => {
    void (async () => {
      try {
        if (isLoadingVerifyEmail) return;
        await verifyEmail({ email, code: data.code });
        setShouldRedirect(true);
      } catch (error) {
        console.error(error);
      }
    })();
  };

  const handleResend = () => {
    void (async () => {
      try {
        if (isLoadingResend || !email) return;
        await resendVerificationCode({ email });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <BrandCardSideContent centerContent>
      <form className="flex flex-col gap-6 w-full" onSubmit={handleSubmit(handleVerify)}>
        <div className="flex flex-col gap-2">
          <h1 className="font-newsreader font-bold text-2xl leading-[117%] text-[rgba(0,0,0,0.87)]">
            {t('registrationPage.successTitle')}
          </h1>
          <p className="font-dm text-sm font-normal leading-[150%] tracking-[0.15px] text-[rgba(0,0,0,0.6)]">
            {t('registrationPage.successDescription')}
          </p>
        </div>
        {isSuccessVerifyEmail && <Alert status="success">{t('verifyEmailPage.successInfo')}</Alert>}
        {isSuccessResend && <Alert status="success">{t('verifyEmailPage.successInfoResendCode')}</Alert>}
        {isErrorVerifyEmail && <Alert status="error">{errorVerifyEmail?.message}</Alert>}
        {isErrorResend && <Alert status="error">{errorResend?.message}</Alert>}
        <div className="flex flex-col gap-3">
          <Controller
            control={control}
            name="code"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <OtpInput
                isInputNum
                placeholder="######"
                error={errors.code?.message}
                onChange={onChange}
                value={value}
                numInputs={6}
                inputClassName={OTP_INPUT_CLASS}
              />
            )}
          />
          <button
            type="button"
            onClick={handleResend}
            className="font-dm text-sm text-[#2196F3] underline text-left w-fit"
          >
            {t('button.resendCode')}
          </button>
        </div>
        <LoadingButton loading={isLoadingVerifyEmail} type="submit" color="primary" className="w-full">
          {t('button.continue')}
        </LoadingButton>
      </form>
    </BrandCardSideContent>
  );
}
